import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Progress } from "./ui/progress";
import { Badge } from "./ui/badge";
import { CalendarDays, CheckCircle2, Clock, AlertTriangle, Users, FileText } from "lucide-react";

const progressData = [
  {
    phase: "Planning & Research",
    progress: 100,
    status: "completed",
    startDate: "2024-01-01",
    endDate: "2024-04-30",
    description: "Initial planning, site analysis, and preliminary research completed"
  },
  {
    phase: "Community Consultation",
    progress: 75,
    status: "in-progress",
    startDate: "2024-05-01",
    endDate: "2024-09-30",
    description: "Public consultation meetings and community survey collection"
  },
  {
    phase: "Design Development",
    progress: 45,
    status: "in-progress",
    startDate: "2024-06-01",
    endDate: "2024-11-30",
    description: "Detailed design development based on community feedback"
  },
  {
    phase: "Council Proposal",
    progress: 0,
    status: "pending",
    startDate: "2024-12-01",
    endDate: "2025-03-31",
    description: "Formal presentation and approval process with local council"
  }
];

const keyMetrics = [
  { label: "Overall Progress", value: "55%", change: "On track", trend: "neutral", icon: FileText },
  { label: "Community Responses", value: "1,247", change: "+89 this week", trend: "up", icon: Users },
  { label: "Public Meetings", value: "8", change: "3 completed", trend: "neutral", icon: CalendarDays },
  { label: "Surveys", value: "3", change: "2 under review", trend: "neutral", icon: FileText }
];

const getStatusIcon = (status) => {
  switch (status) {
    case "completed":
      return <CheckCircle2 className="h-4 w-4 text-green-600" />;
    case "in-progress":
      return <Clock className="h-4 w-4 text-blue-600" />;
    case "pending":
      return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
    default:
      return <Clock className="h-4 w-4 text-gray-600" />;
  }
};

const getStatusBadgeColor = (status) => {
  switch (status) {
    case "completed":
      return "bg-green-100 text-green-800 hover:bg-green-100";
    case "in-progress":
      return "bg-blue-100 text-blue-800 hover:bg-blue-100";
    case "pending":
      return "bg-yellow-100 text-yellow-800 hover:bg-yellow-100";
    default:
      return "bg-gray-100 text-gray-800 hover:bg-gray-100";
  }
};

export function CurrentProgressTab() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1>Current Progress</h1>
          <p className="text-muted-foreground mt-1">
            Track the progress of our park master plan development and community engagement
          </p>
        </div>
        <Badge variant="secondary" className="flex items-center gap-1">
          <CalendarDays className="h-3 w-3" />
          Updated 1 hour ago
        </Badge>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {keyMetrics.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <Card key={index}>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <Icon className="h-4 w-4" />
                  {metric.label}
                </CardDescription>
                <CardTitle className="text-2xl">{metric.value}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{metric.change}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Progress by Phase */}
      <Card>
        <CardHeader>
          <CardTitle>Planning Phase Progress</CardTitle>
          <CardDescription>
            Progress through the key phases of park master plan development
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {progressData.map((phase, index) => (
            <div key={index} className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {getStatusIcon(phase.status)}
                  <h3 className="font-medium">{phase.phase}</h3>
                  <Badge 
                    variant="secondary" 
                    className={getStatusBadgeColor(phase.status)}
                  >
                    {phase.status.replace("-", " ")}
                  </Badge>
                </div>
                <span className="font-medium">{phase.progress}%</span>
              </div>
              
              <Progress value={phase.progress} className="h-2" />
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-muted-foreground">
                <div>
                  <span className="font-medium">Description:</span> {phase.description}
                </div>
                <div>
                  <span className="font-medium">Start:</span> {new Date(phase.startDate).toLocaleDateString()}
                </div>
                <div>
                  <span className="font-medium">End:</span> {new Date(phase.endDate).toLocaleDateString()}
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Current Focus Areas */}
      <Card>
        <CardHeader>
          <CardTitle>Current Focus Areas</CardTitle>
          <CardDescription>What we're working on right now</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border rounded-lg space-y-2">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-blue-600" />
                <h4 className="font-medium">Community Feedback Collection</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Actively gathering community input through surveys, public meetings, and online consultations
              </p>
              <Progress value={75} className="h-2" />
              <p className="text-xs text-muted-foreground">75% of consultation period complete</p>
            </div>
            
            <div className="p-4 border rounded-lg space-y-2">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-green-600" />
                <h4 className="font-medium">Design Development</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Creating detailed designs based on community priorities and technical feasibility studies
              </p>
              <Progress value={45} className="h-2" />
              <p className="text-xs text-muted-foreground">45% of design phase complete</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upcoming Milestones */}
      <Card>
        <CardHeader>
          <CardTitle>Upcoming Milestones</CardTitle>
          <CardDescription>Key dates and events in the coming months</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div>
                <h4 className="font-medium">Final Community Meeting</h4>
                <p className="text-sm text-muted-foreground">Present draft plans and gather final feedback</p>
              </div>
              <Badge variant="outline">Sep 15, 2024</Badge>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div>
                <h4 className="font-medium">Survey Closing</h4>
                <p className="text-sm text-muted-foreground">Last day to submit community survey responses</p>
              </div>
              <Badge variant="outline">Sep 30, 2024</Badge>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div>
                <h4 className="font-medium">Final Design Presentation</h4>
                <p className="text-sm text-muted-foreground">Public presentation of the final master plan design</p>
              </div>
              <Badge variant="outline">Nov 20, 2024</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}